import { useState } from "react";

import { Button, Text, toastError } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import { useLock, useMutationRequest } from "../../../../api/hooks";
import {
  enrollInFPSL,
  fetchAccountAgreement,
  patchEsign,
} from "../../../../api/rest/account";
import { acceptW8, confirmDetails } from "../../../../api/rest/onboarding";
import { isDisclosed } from "../../../../utils";
import { MSLA_AGREEMENT_REVISION } from "../../entityOnboarding/constants";
import { Radio } from "../../entityOnboarding/Radio";
import { Back } from "../back";
import { isFpslEligible } from "../fpslEligibility";
import { clearFpslPreference, getFpslPreference } from "../fpslPreference";
import type { StepProps } from "../session";

interface DataItemsProps {
  title: string;
  children?: string;
  className?: string;
}

const DataItem = ({ title, children, className }: DataItemsProps) => (
  <div className={className}>
    <Text className="font-medium">{title}</Text>
    {children || ""}
  </div>
);

const InactiveRadio = (props: { children: string; selected: boolean }) => (
  <Radio
    name={props.children}
    onChange={() => {}}
    label={props.children}
    isChecked={props.selected}
  />
);

const Three = ({ session }: StepProps) => {
  const lock = useLock();

  const { account, details } = session;
  const { mutate } = useMutationRequest(() => confirmDetails(account.id));

  const [viewedW8, setViewedW8] = useState(false);

  const companyValues = false;
  const isInternational = details?.country_of_tax_residence !== "USA";

  const fetchW8Link = (accountId: string) => {
    fetchAccountAgreement(accountId, "w_8ben").then((resp) => {
      const binaryData = [];
      binaryData.push(resp);
      const fileURL = window.URL.createObjectURL(
        new Blob(binaryData, { type: "application/pdf" }),
      );
      window.open(fileURL);
    });
    setViewedW8(true);
  };

  const attemptFpslEnrollment = async () => {
    const preference = getFpslPreference(account.id);
    if (preference !== true) {
      return;
    }

    const fpslEligible = isFpslEligible(details, session.country.infos);
    if (!fpslEligible) {
      return;
    }

    try {
      await patchEsign(account.id, {
        agreement: "master_securities_lending_agreement",
        revision: MSLA_AGREEMENT_REVISION,
        signed_at: new Date(),
      });
      await enrollInFPSL(account.id);
      AmplitudeProvider.dispatch("algodash_enrolled_fpsl");
    } catch {
      toastError(
        "Stock lending enrollment failed. You can enroll later from your account settings.",
      );
    } finally {
      clearFpslPreference(account.id);
    }
  };

  const onSuccess = async () => {
    lock.unlock();

    await attemptFpslEnrollment();

    // go to next step
    session.next();

    // call amplitude event
    AmplitudeProvider.dispatch("onboardingv2_step_three_complete");
  };

  const onClick = () => {
    if (lock.locked) {
      return;
    }

    lock.lock();

    // skip W8
    if (!isInternational) {
      mutate(null, { onSuccess, onSettled: () => lock.unlock() });
      return;
    }

    // attempt to accept W8
    acceptW8(account.id)
      .catch((err) => toastError(`Error accepting W8: ${err?.message}`))
      .then(() =>
        mutate(null, {
          onSuccess,
          onSettled: () => lock.unlock(),
        }),
      );
  };

  return (
    <div>
      <div className="mb-4 grid w-full grid-cols-2 gap-4">
        <DataItem title="Name">{`${details?.given_name} ${details?.family_name}`}</DataItem>
        <DataItem title="Date of Birth">
          {details?.date_of_birth ?? undefined}
        </DataItem>
        <DataItem title="Address">{details?.street_address?.at(0)}</DataItem>
        <DataItem title="Country of Tax Residence">
          {details?.country_of_tax_residence ?? undefined}
        </DataItem>
        <DataItem title="Phone Number">
          {details?.phone_number ?? undefined}
        </DataItem>
      </div>
      <Text className="mt-5 mb-4 font-medium">
        Do any of the following apply to you or your family?
      </Text>
      {details && (
        <div className="flex flex-col gap-2">
          <InactiveRadio selected={!isDisclosed(details)}>
            None of the below apply to me or my family
          </InactiveRadio>
          <InactiveRadio selected={!!details.is_affiliated_exchange_or_finra}>
            Affiliated or work with a US registered broker dealer or FINRA.
          </InactiveRadio>
          <InactiveRadio selected={!!details.is_control_person}>
            Senior executive at or 10% shareholder of a publicly traded company.
          </InactiveRadio>
          <InactiveRadio selected={!!details.is_politically_exposed}>
            I am a senior political figure.
          </InactiveRadio>
          <InactiveRadio selected={!!details.immediate_family_exposed}>
            I am a family member or relative of a senior political figure.
          </InactiveRadio>
        </div>
      )}
      {companyValues && (
        <div>
          <Text className="mt-5 mb-4 font-medium">Company details</Text>
          <div className="grid w-full grid-cols-2 gap-2">
            <DataItem title="Company Name">Alpaca</DataItem>
            <DataItem title="Company Complience Email">
              complience@company.com
            </DataItem>
            <DataItem title="Company Address">123 Alpaca Street</DataItem>
            <DataItem title="Department Name">Complience</DataItem>
          </div>
        </div>
      )}
      {isInternational && (
        <div>
          <Text className="mt-5 mb-4 font-medium">
            Please read and agree to the W-8BEN form to continue:
          </Text>
          <Button
            className="mr-1 mb-4 w-full bg-[#e6e7ec] font-medium"
            disabled={false}
            onClick={() => fetchW8Link(account.id)}
          >
            Open form W-8BEN
          </Button>
          <div className="flex flex-col gap-4 text-gray-600">
            <Text>
              Under penalties of perjury, I declare that I have examined the
              information on this form and to the best of my knowledge and
              belief it is true, correct, and complete. I further certify under
              penalties of perjury that:
            </Text>
            <Text>
              I am the individual that is the beneficial owner (or am authorized
              to sign for the individual that is the beneficial owner) of all
              the income to which this form relates or am using this form to
              document myself for chapter 4 purposes,
            </Text>
            <Text>
              The person named on line 1 of this form is not a U.S. person, The
              income to which this form relates is: (a) not effectively
              connected with the conduct of a trade or business in the United
              States, (b) effectively connected but is not subject to tax under
              an applicable income tax treaty, or (c) the partner's share of a
              partnership&apos;s effectively connected income, The person named
              on line 1 of this form is a resident of the treaty country listed
              on line 9 of the form (if any) within the meaning of the income
              tax treaty between the United States and that country, and For
              broker transactions or barter exchanges, the beneficial owner is
              an exempt foreign person as defined in the instructions.
            </Text>
            <Text>
              Furthermore, I authorize this form to be provided to any
              withholding agent that has control, receipt, or custody of the
              income of which I am the beneficial owner or any withholding agent
              that can disburse or make payments of the income of which I am the
              beneficial owner. I agree that I will submit a new form within 30
              days if any certification made on this form becomes incorrect.
            </Text>
            <Text>
              I confirm and certify that the information on the above referenced
              W-8BEN is accurate and correct and that I understand that by
              clicking on &ldquo;I Certify&ldquo; below, I am electronically
              signing the W-8BEN.
            </Text>
          </div>
        </div>
      )}
      <div className="my-8 flex items-center justify-center gap-2 rounded-lg border border-gray-200 border-dashed bg-transparent px-[28px] py-[20px]">
        <div className="flex h-[50px] items-center">
          <img
            className="h-[50px]"
            src="resources/images/v2/passport-kyc.png"
            alt="Passport"
          />
        </div>
        <Text className="ml-4">
          You will be asked to submit verification documents in the following
          steps.
        </Text>
      </div>
      <div className="mt-8 mb-16 flex justify-end gap-1">
        <Back session={session} />
        <Button onClick={onClick} disabled={isInternational && !viewedW8}>
          Continue
        </Button>
      </div>
    </div>
  );
};

export default Three;
